import { Link } from 'wouter';
import { CtaBand, Hero, Section } from '../components/ui/primitives';
import { usePageMeta } from '../lib/seo';

/**
 * For Schools. Baseline §3 — 5 sections. Note section 3 ("School Supply Bundles")
 * is the ONE mid-page navy section anywhere on the site; that is deliberate and
 * preserved (P-3).
 */

const NEEDS = [
  'Clean Classrooms',
  'Hygienic Bathrooms',
  'Staffroom Supplies',
  'Monthly Replenishment',
  'Term-Start Stock Prep',
  'Bulk Affordability',
] as const;

const BUNDLES = [
  {
    name: 'Starter School Hygiene Pack',
    tagline: 'Essential setup for a new term or newly built facility block.',
    items: [
      '1x Industrial Mop & Bucket Set',
      '2x Hard Bristle Brooms',
      '5L Pine Disinfectant',
      '5L Toilet Bowl Cleaner',
      '1x 48-Pack Toilet Paper',
    ],
  },
  {
    name: 'Monthly Refill Pack',
    tagline: 'Keeps the essentials topped up between terms.',
    items: ['5L Dishwashing Liquid', '5L Multi-purpose Cleaner', '6-Pack Paper Towels'],
  },
  {
    name: 'Term-Start Replenishment Pack',
    tagline: 'A full reset before learners return.',
    items: ['5L Floor Polish', '5L Window Cleaner', '5L Air Freshener', 'Brooms x5', 'Mops x5'],
  },
] as const;

const STEPS = [
  { title: 'Discuss Needs', body: 'Tell us your term requirements and budget.' },
  { title: 'Formal Quote', body: 'We prepare a compliant quotation for your procurement file.' },
  { title: 'Payment', body: 'Confirm the quote and settle by EFT.' },
  { title: 'Procurement', body: 'We source and prepare your order.' },
  { title: 'Delivery & POD', body: 'Delivered to your school with proof of delivery.' },
] as const;

export function ForSchools() {
  usePageMeta({
    title: 'Solutions for Schools',
    description:
      'Cleaning and hygiene supply bundles built for schools in the Mopani District — term-start stock prep, monthly replenishment and compliant procurement documentation.',
    path: '/for-schools',
  });

  return (
    <>
      <Hero
        title="Solutions for Schools"
        subtitle="Supporting education through clean, hygienic, and safe environments for learners and staff."
        image="/assets/hero-schools.jpg"
      />

      <Section
        variant="white"
        title="Meeting School Needs"
        intro="We tailor our product supply to the unique rhythms and requirements of educational institutions."
      >
        <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {NEEDS.map((n) => (
            <li key={n} className="card px-6 py-4 font-semibold text-navy">
              {n}
            </li>
          ))}
        </ul>
      </Section>

      {/* The one mid-page navy section on the site. Preserved. */}
      <Section
        variant="navy"
        title="School Supply Bundles"
        intro="Pre-configured packages designed for institutional buying. Easy to quote, easy to order."
      >
        <div className="grid gap-8 lg:grid-cols-3">
          {BUNDLES.map((b) => (
            <div
              key={b.name}
              className="rounded-lg border border-white/20 bg-white/5 p-6"
            >
              <h3 className="font-heading text-xl font-bold text-white">{b.name}</h3>
              <p className="mt-2 text-sm italic text-white/70">&ldquo;{b.tagline}&rdquo;</p>
              <ul className="mt-5 space-y-2">
                {b.items.map((i) => (
                  <li key={i} className="flex gap-2 text-sm text-white/85">
                    <span className="text-green-cta" aria-hidden="true">
                      ✓
                    </span>
                    {i}
                  </li>
                ))}
              </ul>
              <Link href="/quote" className="btn btn-primary btn-small mt-6">
                Quote this bundle
              </Link>
            </div>
          ))}
        </div>
      </Section>

      <Section variant="muted" title="Procurement Built for Schools">
        <ol className="grid gap-8 md:grid-cols-3 lg:grid-cols-5">
          {STEPS.map((s, i) => (
            <li key={s.title}>
              <span
                className="flex h-10 w-10 items-center justify-center rounded-full bg-green-cta font-heading text-lg font-bold text-white"
                aria-hidden="true"
              >
                {i + 1}
              </span>
              <h3 className="mt-4 font-heading text-base font-bold text-navy">{s.title}</h3>
              <p className="mt-2 text-sm text-charcoal/80">{s.body}</p>
            </li>
          ))}
        </ol>
      </Section>

      <section className="section bg-white">
        <div className="container-content text-center">
          <h2 className="font-heading text-3xl font-bold text-navy lg:text-[36px]">
            Need compliance documents?
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-lg text-charcoal/80">
            We can supply our CSD registration, tax clearance and B-BBEE documentation with any
            quotation for your procurement file.
          </p>
          <div className="mt-8 flex justify-center">
            <Link href="/contact" className="btn btn-navy btn-hero">
              Contact Us
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
